if (!customElements.get("video-reels")) {
  customElements.define(
    "video-reels",
    class VideoReels extends HTMLElement {
      constructor() {
        super();
        this.activeClass = "active";
        this.swiper = null;
        this._reelsAbort = null;
        this._scrollRaf = 0;
        this._resizeRaf = 0;
        this._onScrollBound = null;
        this._reelsResizeObserver = null;
        this.checkInterval = null;
        this._loopSettleTimer = null;
        this._onScrollendBound = null;
        this._reelRealSlides = null;
        this._reelBufferTeleportAt = 0;
      }

      connectedCallback() {
        this._sectionObserver = null;
        this.init();
      }

      disconnectedCallback() {
        if (this._sectionObserver) {
          this._sectionObserver.disconnect();
          this._sectionObserver = null;
        }
        if (this.checkInterval) clearInterval(this.checkInterval);
        this._teardownCssScrollReels();
      }

      _teardownCssScrollReels() {
        this._reelsAbort?.abort();
        this._reelsAbort = null;
        if (this._scrollRaf) {
          cancelAnimationFrame(this._scrollRaf);
          this._scrollRaf = 0;
        }
        if (this._resizeRaf) {
          cancelAnimationFrame(this._resizeRaf);
          this._resizeRaf = 0;
        }
        this._reelsResizeObserver?.disconnect();
        this._reelsResizeObserver = null;
        if (this._loopSettleTimer) {
          clearTimeout(this._loopSettleTimer);
          this._loopSettleTimer = null;
        }
        this._reelRealSlides = null;
        this._reelBufferTeleportAt = 0;
        const sr = this.querySelector("[data-scroll-reels]");
        sr?.querySelectorAll(".video-carousel[data-reel-clone]").forEach((el) => {
          el.remove();
        });
        if (sr) delete sr.dataset.reelBufferClones;
      }

      _normalizeReelCloneSubtree(rootEl) {
        for (const a of [...rootEl.attributes]) {
          if (a.name.includes("shopify")) {
            rootEl.removeAttribute(a.name);
          }
        }
        rootEl.querySelectorAll("[id]").forEach((node) => {
          node.removeAttribute("id");
        });
        rootEl.querySelectorAll("video").forEach((video) => {
          video.pause();
          video.muted = true;
          video.removeAttribute("autoplay");
          video.autoplay = false;
        });
      }

      _reelTeleportCloneToReal(scrollRoot, cloneEl, realEl, carousel) {
        const prevSnap = scrollRoot.style.scrollSnapType;
        scrollRoot.style.scrollSnapType = "none";
        const cR = cloneEl.getBoundingClientRect();
        const rR = realEl.getBoundingClientRect();
        scrollRoot.scrollLeft += rR.left - cR.left;
        scrollRoot.offsetHeight;
        scrollRoot.style.scrollSnapType = prevSnap || "";
        this._reelBufferTeleportAt = performance.now();
        this.syncActiveFromScroll(scrollRoot, carousel);
      }

      /**
       * After scroll settles: if viewport is centered on a buffer clone, jump to the matching real slide (same pixels).
       */
      _applyReelBufferTeleport(scrollRoot, carousel) {
        if (scrollRoot.dataset.reelBufferClones !== "true") return;
        const reals = this._reelRealSlides;
        if (!reals?.length) return;

        if (performance.now() - this._reelBufferTeleportAt < 120) return;

        const view = scrollRoot.clientWidth;
        const cent = scrollRoot.scrollLeft + view / 2;

        let bestClone = null;
        let bestReal = null;
        let bestDist = Infinity;

        scrollRoot
          .querySelectorAll(":scope > .video-carousel[data-reel-clone]")
          .forEach((clone) => {
            const mid = clone.offsetLeft + clone.offsetWidth / 2;
            const d = Math.abs(mid - cent);
            if (d < bestDist && d < clone.offsetWidth * 0.48) {
              bestDist = d;
              bestClone = clone;
              const src = Number(clone.dataset.reelCloneSourceIndex);
              bestReal = reals[src];
            }
          });

        if (!bestClone || !bestReal || !scrollRoot.contains(bestReal)) return;

        const cR = bestClone.getBoundingClientRect();
        const rR = bestReal.getBoundingClientRect();
        if (Math.abs(cR.left - rR.left) < 1.5 && Math.abs(cR.width - rR.width) < 1.5) {
          return;
        }

        this._reelTeleportCloneToReal(scrollRoot, bestClone, bestReal, carousel);
      }

      /**
       * Head: clones of last K (left of first real). Tail: clones of first K (right of last real). K ≤ 2.
       */
      _setupReelBufferClones(scrollRoot, carousel) {
        if (scrollRoot.dataset.reelBufferClones === "true") return;

        const reals = Array.from(
          scrollRoot.querySelectorAll(
            ":scope > .video-carousel:not([data-reel-clone])",
          ),
        );
        const n = reals.length;
        if (n < 1) return;

        const k = Math.min(2, n);
        this._reelRealSlides = reals.slice();

        for (let i = n - 1; i >= n - k; i--) {
          const c = reals[i].cloneNode(true);
          c.dataset.reelClone = "head";
          c.dataset.reelCloneSourceIndex = String(i);
          c.removeAttribute("data-reel-pass");
          this._normalizeReelCloneSubtree(c);
          scrollRoot.prepend(c);
        }

        for (let i = 0; i < k; i++) {
          const c = reals[i].cloneNode(true);
          c.dataset.reelClone = "tail";
          c.dataset.reelCloneSourceIndex = String(i);
          c.removeAttribute("data-reel-pass");
          this._normalizeReelCloneSubtree(c);
          scrollRoot.appendChild(c);
        }

        scrollRoot.dataset.reelBufferClones = "true";
        scrollRoot.offsetHeight;

        const first = reals[0];
        if (scrollRoot.scrollWidth > scrollRoot.clientWidth + 4) {
          scrollRoot.scrollLeft = Math.max(
            0,
            first.offsetLeft +
              first.offsetWidth / 2 -
              scrollRoot.clientWidth / 2,
          );
        }
        this.syncActiveFromScroll(scrollRoot, carousel);
      }

      observeSection() {
        const observerOptions = {
          root: null,
          rootMargin: "0px",
          threshold: 0.1,
        };

        const sectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = this.querySelector(".video-carousel.active video");

            if (video) {
              if (entry.isIntersecting) {
                video.play();
              } else {
                video.pause();
              }
            }
          });
        }, observerOptions);

        sectionObserver.observe(this);
        this._sectionObserver = sectionObserver;
      }

      hardStopAllExceptActive(activeSlide) {
        this.querySelectorAll(".video-carousel").forEach((slide) => {
          const video = slide.querySelector("video");
          if (!video) return;

          if (slide !== activeSlide) {
            video.pause();
            video.muted = true;
            video.removeAttribute("autoplay");
            video.autoplay = false;
          }
        });
      }

      sanitizeVideosOnce() {
        if (this._sanitized) return;
        this._sanitized = true;
        this.querySelectorAll("video").forEach((video) => {
          video.pause();
          video.muted = true;
          video.removeAttribute("autoplay");
          video.autoplay = false;
          video.setAttribute("playsinline", "");
          video.setAttribute("webkit-playsinline", "");
          video.preload = "metadata";
        });
      }

      playVideoInActiveSlide(carousel, activeSlide) {
        const sound = carousel?.dataset?.sound;
        const activeVideo = activeSlide?.querySelector("video");
        if (activeVideo) {
          activeVideo.muted = sound !== "on";
          setTimeout(() => {
            activeVideo.play().catch((err) => {
              console.warn("Autoplay was prevented:", err);
            });
          }, 100);
        }
      }

      slidesInOrder(scrollRoot) {
        return Array.from(scrollRoot.querySelectorAll(":scope > .video-carousel"));
      }

      getClosestSlideIndex(scrollRoot) {
        const slides = this.slidesInOrder(scrollRoot);
        if (!slides.length) return 0;
        const mid =
          scrollRoot.getBoundingClientRect().left + scrollRoot.clientWidth / 2;
        let best = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
          const r = slide.getBoundingClientRect();
          const c = r.left + r.width / 2;
          const d = Math.abs(c - mid);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });
        return best;
      }

      syncActiveFromScroll(scrollRoot, carousel) {
        const slides = this.slidesInOrder(scrollRoot);
        if (!slides.length) return;
        const idx = this.getClosestSlideIndex(scrollRoot);
        const active = slides[idx];
        slides.forEach((s) => {
          s.classList.toggle(this.activeClass, s === active);
        });
        this.hardStopAllExceptActive(active);
        this.playVideoInActiveSlide(carousel, active);
      }

      scrollStep(scrollRoot) {
        const s0 =
          this._reelRealSlides?.[0] ||
          scrollRoot.querySelector(
            ":scope > .video-carousel:not([data-reel-clone])",
          );
        if (!s0) return 316;
        const st = getComputedStyle(scrollRoot);
        const gap = parseFloat(st.columnGap || st.gap) || 16;
        return s0.offsetWidth + gap;
      }

      initCssScrollReels(scrollRoot) {
        this._teardownCssScrollReels();
        this._reelsAbort = new AbortController();
        const signal = this._reelsAbort.signal;

        const carousel = this.querySelector(".wt-slider__container--video-carousel");
        this.swiper = carousel;
        if (!carousel?.dataset.sound) {
          carousel.dataset.sound = "off";
        }

        this.sanitizeVideosOnce();

        const settleLoop = () => {
          requestAnimationFrame(() => {
            this._applyReelBufferTeleport(scrollRoot, carousel);
            this.syncActiveFromScroll(scrollRoot, carousel);
          });
        };

        this._onScrollendBound = () => {
          if (this._loopSettleTimer) {
            clearTimeout(this._loopSettleTimer);
            this._loopSettleTimer = null;
          }
          settleLoop();
        };
        scrollRoot.addEventListener("scrollend", this._onScrollendBound, {
          passive: true,
          signal,
        });

        this._onScrollBound = () => {
          if (this._scrollRaf) return;
          this._scrollRaf = requestAnimationFrame(() => {
            this._scrollRaf = 0;
            this.syncActiveFromScroll(scrollRoot, carousel);
          });
          if (this._loopSettleTimer) clearTimeout(this._loopSettleTimer);
          this._loopSettleTimer = setTimeout(() => {
            this._loopSettleTimer = null;
            settleLoop();
          }, 200);
        };

        scrollRoot.addEventListener("scroll", this._onScrollBound, {
          passive: true,
          signal,
        });

        this._reelsResizeObserver = new ResizeObserver(() => {
          if (this._resizeRaf) return;
          this._resizeRaf = requestAnimationFrame(() => {
            this._resizeRaf = 0;
            this._applyReelBufferTeleport(scrollRoot, carousel);
            this.syncActiveFromScroll(scrollRoot, carousel);
          });
        });
        this._reelsResizeObserver.observe(scrollRoot);

        const slideshow = this.querySelector("slideshow-section");
        const next = slideshow?.querySelector(".wt-slider__nav-next--featured");
        const prev = slideshow?.querySelector(".wt-slider__nav-prev--featured");

        const step = () => this.scrollStep(scrollRoot);

        next?.addEventListener(
          "click",
          () => {
            scrollRoot.scrollBy({ left: step(), behavior: "smooth" });
          },
          { signal },
        );
        prev?.addEventListener(
          "click",
          () => {
            scrollRoot.scrollBy({ left: -step(), behavior: "smooth" });
          },
          { signal },
        );

        const onNavKey = (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            (e.currentTarget === next ? next : prev)?.click();
          }
        };
        next?.addEventListener("keydown", onNavKey, { signal });
        prev?.addEventListener("keydown", onNavKey, { signal });

        scrollRoot.addEventListener(
          "keydown",
          (e) => {
            if (e.key === "ArrowRight") {
              e.preventDefault();
              scrollRoot.scrollBy({ left: step(), behavior: "smooth" });
            } else if (e.key === "ArrowLeft") {
              e.preventDefault();
              scrollRoot.scrollBy({ left: -step(), behavior: "smooth" });
            }
          },
          { signal },
        );

        carousel.addEventListener(
          "click",
          (e) => {
            const btn = e.target.closest(".wt-video__sound-toggle");
            if (!btn) return;
            const activeSlide = scrollRoot.querySelector(
              `.video-carousel.${this.activeClass}`,
            );
            const activeVideo = activeSlide?.querySelector("video");
            if (!activeVideo) return;

            this.hardStopAllExceptActive(activeSlide);

            const soundOn = carousel.dataset.sound === "on";
            const nextSoundOn = !soundOn;
            carousel.dataset.sound = nextSoundOn ? "on" : "off";
            activeVideo.muted = !nextSoundOn;

            activeVideo.setAttribute("playsinline", "");
            activeVideo.setAttribute("webkit-playsinline", "");
            activeVideo.removeAttribute("autoplay");
            activeVideo.autoplay = false;

            setTimeout(() => {
              activeVideo.play().catch(() => {});
            }, 80);
          },
          { signal },
        );

        requestAnimationFrame(() => {
          this._setupReelBufferClones(scrollRoot, carousel);
        });

        this.observeSection();
      }

      checkSwiperInitialization() {
        const swiperContainer = this.querySelector("[data-swiper]");
        const mySwiperInstance = swiperContainer?.swiper;

        if (
          swiperContainer?.classList.contains("swiper-initialized") &&
          mySwiperInstance
        ) {
          if (!swiperContainer.dataset.sound) {
            swiperContainer.dataset.sound = "off";
          }
          this.swiper = swiperContainer;
          this.sanitizeVideosOnce();
          this.addVideoEventHandlersSwiper(mySwiperInstance);
          this.handleSlideChangeSwiper(mySwiperInstance);
          this.handleSoundToggleSwiper(mySwiperInstance);
          this.observeSectionSwiper();
          clearInterval(this.checkInterval);
        }
      }

      findActiveSlideSwiper(swiper) {
        return swiper.slides[swiper.activeIndex];
      }

      toggleActiveClassSwiper(swiper) {
        const activeSlide = this.findActiveSlideSwiper(swiper);
        swiper.slides.forEach((slide) => {
          slide.classList.remove(this.activeClass);
          if (activeSlide === slide) {
            slide.classList.add(this.activeClass);
          }
        });
      }

      handleSlideChangeSwiper(swiper) {
        this.hardStopAllExceptActive(this.findActiveSlideSwiper(swiper));
        this.toggleActiveClassSwiper(swiper);
        this.playVideoInActiveSlide(swiper.el, this.findActiveSlideSwiper(swiper));
      }

      handleSoundToggleSwiper(swiper) {
        swiper.slides.forEach((slide) => {
          const button = slide.querySelector(".wt-video__sound-toggle");
          if (!button || button._bound) return;
          button._bound = true;

          button.addEventListener(
            "click",
            () => {
              const activeSlide = swiper.slides[swiper.activeIndex];
              const activeVideo = activeSlide?.querySelector("video");
              if (!activeVideo) return;

              this.hardStopAllExceptActive(activeSlide);

              const soundOn = swiper.el.dataset.sound === "on";
              const nextSoundOn = !soundOn;
              swiper.el.dataset.sound = nextSoundOn ? "on" : "off";
              activeVideo.muted = !nextSoundOn;

              activeVideo.setAttribute("playsinline", "");
              activeVideo.setAttribute("webkit-playsinline", "");
              activeVideo.removeAttribute("autoplay");
              activeVideo.autoplay = false;

              setTimeout(() => {
                activeVideo.play().catch(() => {});
              }, 80);
            },
            { passive: true },
          );
        });
      }

      addVideoEventHandlersSwiper(swiper) {
        this._onSlideChange = () => this.handleSlideChangeSwiper(swiper);
        swiper.on("slideChange", this._onSlideChange);
        swiper.on("slidesLengthChange", () => this.handleSoundToggleSwiper(swiper));
        swiper.on("update", () => this.handleSoundToggleSwiper(swiper));
      }

      observeSectionSwiper() {
        const observerOptions = {
          root: null,
          rootMargin: "0px",
          threshold: 0.1,
        };

        const sectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = this.querySelector(".swiper-slide-active video");

            if (video) {
              if (entry.isIntersecting) {
                video.play();
              } else {
                video.pause();
              }
            }
          });
        }, observerOptions);

        sectionObserver.observe(this);
        this._sectionObserver = sectionObserver;
      }

      init() {
        const scrollRoot = this.querySelector("[data-scroll-reels]");
        if (scrollRoot) {
          this.initCssScrollReels(scrollRoot);
          return;
        }

        this.checkInterval = setInterval(
          this.checkSwiperInitialization.bind(this),
          250,
        );
      }
    },
  );
}
